import { z } from 'zod';
import {
  ApplicationProtocolSchema,
  ExternalNetworkReachabilityStatusSchema,
  ScopeSchema,
  TransportProtocolSchema,
  VhostBindingSourceSchema,
} from '@terra/shared-schemas-orchestrator';

export const ExternalNetworkDiscoverySourceTypeSchema = z.enum([
  'USER_PROVIDED',
  'CLOUD_INVENTORY',
  'ACTIVE_SCAN',
  'REVERSE_DNS',
  'SERVICE_DETECTION',
]);

export type ExternalNetworkDiscoverySourceType = z.infer<
  typeof ExternalNetworkDiscoverySourceTypeSchema
>;

export const AssetDetectionMetricsSchema = z.object({
  activeFindingsCount: z.int().nonnegative(),
  inactiveFindingsCount: z.int().nonnegative(),
  allFindingsCount: z.int().nonnegative(),
  activeSignalsCount: z.int().nonnegative(),
  inactiveSignalsCount: z.int().nonnegative(),
  signalsCount: z.int().nonnegative(),
});

export const ServedNameDtoSchema = z.object({
  domainId: z.string(),
  fqdn: z.string(),
  source: VhostBindingSourceSchema,
});

export const HostGeoSchema = z.object({
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  region: z.string().nullish(),
  city: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  asn: z.string().nullish(),
  asnOrg: z.string().nullish(),
});

export const HostDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  netblockId: z.string(),
  ipAddress: z.string(),
  hostnames: z.array(z.string()).default([]),
  resolvedDomainIds: z.array(z.string()).default([]),
  servedNames: z.array(ServedNameDtoSchema).optional(),
  cloudProvider: z.string().nullable(),
  geo: HostGeoSchema.nullable().optional(),
  cloudAllocationId: z.string().nullable().optional(),
  inventoryLinked: z.boolean().optional(),
  deprovisionedAt: z.coerce.date().nullable().optional(),
  reachabilityStatus:
    ExternalNetworkReachabilityStatusSchema.default('UNKNOWN'),
  lastReachabilityCheckedAt: z.coerce.date().nullable().optional(),
  lastReachableAt: z.coerce.date().nullable().optional(),
  lastUnreachableAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  detectionMetrics: AssetDetectionMetricsSchema.optional(),
  openPorts: z.array(z.int()).optional(),
  products: z.array(z.string()).optional(),
  netblockCidr: z.string().optional(),
  sourceType: ExternalNetworkDiscoverySourceTypeSchema.nullable().optional(),
});

export const NetworkServiceDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  surfaceId: z.string(),
  hostId: z.string().nullable(),
  gatewayId: z.string().nullable().optional(),
  hostIp: z.string().optional(),
  hostnames: z.array(z.string()).optional(),
  gatewayHostname: z.string().optional(),
  port: z.int(),
  transportProtocol: TransportProtocolSchema,
  applicationProtocol: ApplicationProtocolSchema,
  tlsEnabled: z.boolean(),
  banner: z.string().nullable(),
  reachabilityStatus:
    ExternalNetworkReachabilityStatusSchema.default('UNKNOWN'),
  lastReachabilityCheckedAt: z.coerce.date().nullable().optional(),
  lastReachableAt: z.coerce.date().nullable().optional(),
  lastUnreachableAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  detectionMetrics: AssetDetectionMetricsSchema.optional(),
  sourceType: ExternalNetworkDiscoverySourceTypeSchema.nullable().optional(),
  scope: ScopeSchema.optional(),
  effectiveScope: ScopeSchema.optional(),
});
